/**
 * Der **Handgriff an der Stelle**: die vier benannten Auswahlen, mit denen ein
 * Mensch eine Stelle bewertet, dazu die Rücknahme und die eine Rückfrage
 * (ADR-0022; Spec v1.4, Stories 254 bis 258).
 *
 * Er steht in einer eigenen Datei, weil es ihn an **zwei** Flächen gibt: im
 * Befundverlauf, wo der Code steht, und in der Gegenüberstellung, die seit
 * ADR-0022 eine Fläche unter mehreren ist und keine Schranke davor. Zwei
 * Fassungen wären zwei Handgriffe — dieselben vier Wörter, bis eine davon
 * nachgezogen wird und die andere nicht, und dieselbe Rückfrage vor dem fremden
 * „behoben", bis eine der beiden sie vergisst.
 *
 * Was er **nicht** kennt, ist die Fläche um ihn herum: Rand, Abstand und
 * Trennlinie setzt, wer ihn einsetzt. Im Befundverlauf steht er im Kopf der
 * Stelle, unter einer Haarlinie; in der Gegenüberstellung unter den zwei Spalten
 * einer Zuordnung, je Anker einer. Derselbe Handgriff, zwei Nachbarschaften.
 */

import type { ReactElement } from "react";

import { Button, Dialog, Modal, StatusPill } from "../../design-system";
import type { Finding, ForeignFixError, SiteAnchor, Verdict } from "../../api/client";
import { formatTimestamp } from "../../formatting";
import {
  judgedSite,
  judgementOfSite,
  supersededSite,
  VERDICT_GLYPHS,
  VERDICT_LABELS,
  VERDICT_TITLES,
  VERDICTS,
  verdictOfSite,
  WITHDRAW_VERDICT_TITLE,
} from "../findings";

/**
 * Warum an einer überschriebenen Stelle kein Handgriff steht.
 *
 * „Überschrieben" setzt der Poller, still, wenn eine spätere Revision die
 * verankerten Zeilen geändert hat. Der Server nimmt an so einer Stelle nichts
 * mehr an; ein Knopf, der dort nichts bewirkte, behauptete, ein Mensch könnte
 * hier noch etwas sagen.
 */
const SUPERSEDED_SITE =
  "Diese Stelle ist überschrieben — eine spätere Revision hat ihre Zeilen geändert. "
  + "Bewerten lässt sich hier nichts mehr; die Befunde bleiben als Bestand stehen.";

/** Der Titel der einen Rückfrage — die eine Ausnahme von „wer setzen darf, darf ändern". */
const OVERWRITE_FIX = 'Fremdes „behoben" überschreiben';

/**
 * Und warum überhaupt gefragt wird. Der Satz nennt den Grund und nicht die
 * Regel: `fixed` trägt den Beleg über einen Agenten (ADR-0013), und ihn im
 * Vorbeigehen zu löschen wäre nicht Aufräumen, sondern Bestandsverlust.
 */
const OVERWRITE_FIX_HINT =
  'An dieser Stelle steht das „behoben" eines anderen. Es ist die Aussage, dass die Befunde '
  + "hier zutrafen, und der stärkste Beleg über einen Agenten, den dieses System kennt. "
  + "Wird darüber geschrieben, ist er weg.";

/**
 * Der Handgriff, den der Server abgewiesen hat, samt der Antwort, mit der er es
 * getan hat.
 *
 * Beides zusammen, weil die Bestätigung **denselben** Handgriff nachschickt und
 * nicht einen neuen: Wer zwischen Rückfrage und Bestätigung etwas anderes
 * schickte, überschriebe das fremde „behoben" mit etwas, das nie zur Frage
 * stand. Festgehalten wird der **Anker** und nicht die Stelle, aus der er
 * abgelesen wurde: Er ist alles, was der zweite Versuch braucht.
 */
export interface Overwrite {
  anchor: SiteAnchor;
  verdict: Verdict;
  refused: ForeignFixError;
}

/**
 * Der **Handgriff an der Stelle** (ADR-0022; Spec v1.4, Stories 254 bis 258).
 *
 * Er greift für die **Stelle**: Ein Handgriff an einem der Befunde darunter ließe
 * die Zeile zur Hälfte unbeurteilt zurück, und das wäre nicht das, was der
 * Anwender vor sich hat.
 *
 * **Vier benannte Knöpfe, nicht zwei Felder.** Jeder sagt in seiner
 * Beschriftung, was er behauptet, und im Titel, was daraus folgt. Genau das
 * trennt sie von der Nebenwirkung, gegen die ADR-0017 sich einmal gewehrt hat:
 * Hier verschwindet nichts *still*.
 *
 * **Ohne jede Vorbedingung.** Keine Abgabe, keine Gegenüberstellung — der
 * menschliche Befund ergänzt den Vorgang, er trägt ihn nicht (Story 256).
 *
 * **Nichts steht hier für „überschrieben".** Das setzt das System, still; wo nur
 * noch Überschriebenes liegt, steht statt der Knöpfe der Satz, warum.
 */
export function VerdictBar({
  found,
  busy,
  onDecide,
  onWithdraw,
}: {
  /** Die Befunde **eines Ankers** — der Handgriff gilt für sie zusammen. */
  found: Finding[];
  busy: boolean;
  onDecide: (verdict: Verdict) => void;
  onWithdraw: () => void;
}): ReactElement {
  const set = verdictOfSite(found);
  const judged = judgementOfSite(found);

  if (supersededSite(found)) {
    return (
      <p
        style={{
          margin: 0,
          maxWidth: "84ch",
          fontSize: "var(--fs-11)",
          lineHeight: "var(--lh-normal)",
          color: "var(--fg-6)",
          textWrap: "pretty",
        }}
      >
        {SUPERSEDED_SITE}
      </p>
    );
  }

  return (
    <div
      role="group"
      aria-label="Bewertung der Stelle"
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "var(--sp-4)",
      }}
    >
      {/* Links steht, wie es um die Stelle steht, rechts, was sich daran tun
          lässt — dieselbe Aufteilung wie in der Kopfzeile darüber. Ohne Urteil
          bleibt die linke Seite leer: „nicht bewertet" stünde an jeder Stelle
          und sagte nichts. */}
      {set === null ? null : (
        <StatusPill tone="blue">{`Bewertet als „${VERDICT_LABELS[set]}"`}</StatusPill>
      )}

      {judged === null ? null : (
        <span
          style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)", textWrap: "pretty" }}
        >
          {judged}
        </span>
      )}

      <span style={{ flex: 1 }} />

      {VERDICTS.map((verdict) => (
        <Button
          key={verdict}
          // Der gesetzte Handgriff steht ausgefüllt da und die übrigen als
          // Haarlinie: Er ist der Zustand, sie sind die Wahl.
          variant={verdict === set ? "primary" : "outline"}
          size="sm"
          disabled={busy}
          title={VERDICT_TITLES[verdict]}
          icon={VERDICT_GLYPHS[verdict]}
          onClick={() => onDecide(verdict)}
        >
          {VERDICT_LABELS[verdict]}
        </Button>
      ))}

      {/* Nur, wo es etwas zurückzunehmen gibt — und das ist mehr als ein
          gesetzter Handgriff: Auch ein Stand, den keiner der vier benennt, ist
          einer, aus dem ein Weg zurück nach „unbeurteilt" führen muss. Steht die
          Stelle unbeurteilt da, wäre der Knopf ein Weg zu dem Zustand, in dem
          sie schon ist. */}
      {judgedSite(found) ? (
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          title={WITHDRAW_VERDICT_TITLE}
          icon="undo-2"
          onClick={onWithdraw}
        >
          Zurücknehmen
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Die **eine Rückfrage** (ADR-0022; Spec v1.4, Story 258).
 *
 * Sie nennt Person und Zeitpunkt — ohne die beiden wäre sie eine Warnung, mit
 * ihnen ist sie eine Auskunft, auf die sich entscheiden lässt. Jeder andere
 * Handgriff ist ein direkter Klick, auch das Überschreiben einer fremden
 * Bewertung, die kein „behoben" ist: Geschützt ist die eine Aussage, die den
 * Beleg trägt, und nicht der Wert an sich.
 */
export function OverwriteDialog({
  overwriting,
  busy,
  onClose,
  onConfirm,
}: {
  overwriting: Overwrite;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}): ReactElement {
  const { fixedBy, fixedAt } = overwriting.refused;

  return (
    <Modal onClose={onClose}>
      <Dialog
        title={OVERWRITE_FIX}
        description={OVERWRITE_FIX_HINT}
        width={560}
        onClose={onClose}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={onClose}>
              Abbrechen
            </Button>
            <Button variant="primary" size="lg" disabled={busy} onClick={onConfirm}>
              Überschreiben
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-4)" }}>
          <span style={{ fontSize: "var(--fs-12)", color: "var(--fg-1)" }}>
            {fixedAt === null
              ? `Behoben von ${fixedBy}.`
              : `Behoben von ${fixedBy} am ${formatTimestamp(fixedAt)}.`}
          </span>
          <span style={{ fontSize: "var(--fs-12)", color: "var(--text-body)", textWrap: "pretty" }}>
            {`Die Stelle steht danach auf „${VERDICT_LABELS[overwriting.verdict]}".`}
          </span>
        </div>
      </Dialog>
    </Modal>
  );
}
